import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { hasAdminAccess } from "@/lib/admin-access";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getReferralSource, REFERRAL_LABELS, type ReferralSource } from "@/lib/referral-tracker";
import { trackEvent } from "@/lib/analytics";
import logo from "@/assets/logo.webp";

const HEARD_FROM_OPTIONS: { value: ReferralSource; label: string }[] = [
  { value: "whatsapp", label: `💬 ${REFERRAL_LABELS.whatsapp}` },
  { value: "linkedin", label: `💼 ${REFERRAL_LABELS.linkedin}` },
  { value: "friend", label: `👥 ${REFERRAL_LABELS.friend}` },
  { value: "google", label: `🔍 ${REFERRAL_LABELS.google} / Direct Website` },
  { value: "instagram", label: `📸 ${REFERRAL_LABELS.instagram}` },
  { value: "other", label: `📌 ${REFERRAL_LABELS.other}` },
];

export const Route = createFileRoute("/auth")({
  validateSearch: (s: Record<string, unknown>): { redirect?: string; google?: boolean } => ({
    redirect: (s.redirect as string) || "/",
    google: s.google === "1",
  }),
  head: () => ({
    meta: [
      { title: "Sign in - WeLivdIt" },
      {
        name: "description",
        content:
          "Sign in to your WeLivdIt account to track your F-1 visa prep, DS-160 review, mock interviews and readiness reports.",
      },
      { property: "og:title", content: "Sign in - WeLivdIt" },
      {
        property: "og:description",
        content: "Access your WeLivdIt student dashboard for F-1 visa interview prep and DS-160 support.",
      },
      { name: "twitter:title", content: "Sign in - WeLivdIt" },
      {
        name: "twitter:description",
        content: "Access your WeLivdIt student dashboard for F-1 visa interview prep and DS-160 support.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AuthPage,
});

async function routeAfterAuth(navigate: ReturnType<typeof useNavigate>, fallback: string) {
  const { data: sess } = await supabase.auth.getSession();
  if (!sess.session) return;
  if (await hasAdminAccess(sess.session)) {
    navigate({ to: "/admin", replace: true });
    return;
  }
  // After sign-in / sign-up: send everyone to the home page (highlighted Dashboard
  // button in the header lets them jump to /student-dashboard when they're ready).
  const target = fallback.startsWith("/admin") || fallback === "/student-dashboard" ? "/" : fallback;
  navigate({ to: target, replace: true });
}

function AuthPage() {
  const navigate = useNavigate();
  const { redirect = "/", google = false } = useSearch({ from: "/auth" });
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [heardFrom, setHeardFrom] = useState<string>("");

  // Pre-select the channel the visitor actually arrived from (WhatsApp link,
  // LinkedIn post, Google search ...). They can still change it.
  useEffect(() => {
    const detected = getReferralSource();
    if (detected && detected !== "direct") {
      setHeardFrom(HEARD_FROM_OPTIONS.some((o) => o.value === detected) ? detected : "other");
    } else {
      setHeardFrom("google");
    }
  }, []);


  const handleGoogle = useCallback(async () => {
    const isFramed = window.self !== window.top;
    if (isFramed && !google) {
      window.open(
        `/auth?redirect=${encodeURIComponent(redirect)}&google=1`,
        "_blank",
        "noopener,noreferrer",
      );
      toast.info("Google sign-in opened in a new tab.");
      return;
    }

    setLoading(true);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/auth?redirect=${encodeURIComponent(redirect)}`,
      extraParams: { prompt: "select_account" },
    });
    if (result.error) {
      setLoading(false);
      toast.error("Google sign-in failed. Please use email sign-in or try again.");
      return;
    }
    if (result.redirected) return;
    await routeAfterAuth(navigate, redirect);
    setLoading(false);
  }, [google, navigate, redirect]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) routeAfterAuth(navigate, redirect);
      else if (google) void handleGoogle();
    });
  }, [navigate, redirect, google, handleGoogle]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      toast.error("Invalid credentials.");
      return;
    }
    await routeAfterAuth(navigate, redirect);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[0-9 +()-]{6,20}$/.test(phone)) {
      toast.error("Enter a valid phone number.");
      return;
    }
    setLoading(true);
    const redirectUrl = `${window.location.origin}/`;
    const source = heardFrom || getReferralSource();
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { name: name.trim(), phone: phone.trim(), referral_source: source },
      },
    });
    setLoading(false);
    if (error) {
      toast.error(error.message || "Couldn't create account. Please check your details and try again.");
      return;
    }
    trackEvent("signup_completed", { heard_from: source });
    toast.success("Account created. Check your email to confirm, then sign in.");
  };

  return (
    <section className="relative min-h-[88vh] grid place-items-center px-5 py-16 overflow-hidden">
      {/* Ambient background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-hero"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-72 w-[90%] max-w-2xl rounded-full opacity-50 blur-3xl"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, color-mix(in oklab, var(--color-primary) 35%, transparent), transparent 70%)",
        }}
      />

      <div className="w-full max-w-md animate-fade-in">
        <div className="rounded-3xl border border-border bg-card/90 backdrop-blur-md p-8 shadow-soft">
          {/* Logo + heading */}
          <div className="flex flex-col items-center text-center">
            <div className="h-16 w-16 rounded-2xl bg-white grid place-items-center shadow-soft ring-1 ring-border">
              <img
                src={logo}
                alt="WeLivdIt"
                className="h-12 w-12 rounded-xl object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
            <h1 className="mt-5 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
              Welcome to <span className="text-primary">WeLivdIt</span>
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Sign in to access your prep dashboard and booked sessions.
            </p>
          </div>

          {/* Primary - Google */}
          <Button
            type="button"
            onClick={handleGoogle}
            disabled={loading}
            className="mt-7 w-full rounded-full gap-2 h-11 bg-gradient-cta text-primary-foreground hover:opacity-90"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <svg viewBox="0 0 24 24" className="h-4 w-4">
                  <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z" opacity=".95" />
                  <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.28-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23Z" opacity=".7" />
                  <path fill="#fff" d="M5.85 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.35-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.95l3.67-2.84Z" opacity=".55" />
                  <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.67 2.84C6.72 7.31 9.14 5.38 12 5.38Z" opacity=".85" />
                </svg>
                Continue with Google
              </>
            )}
          </Button>

          {/* Separator */}
          <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
          </div>

          {/* Email tabs */}
          <Tabs defaultValue="signin">
            <TabsList className="grid grid-cols-2 rounded-full">
              <TabsTrigger value="signin" className="rounded-full">Sign in</TabsTrigger>
              <TabsTrigger value="signup" className="rounded-full">Create account</TabsTrigger>
            </TabsList>
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input id="signin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signin-pw">Password</Label>
                  <Input id="signin-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-full h-11">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in with email"}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label htmlFor="su-name">Full name</Label>
                  <Input id="su-name" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="su-phone">Phone (same as used while booking)</Label>
                  <Input id="su-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 …" required />
                  <p className="text-xs text-muted-foreground">
                    We use this to link your bookings to your dashboard.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="su-email">Email</Label>
                  <Input id="su-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="su-pw">Password (min 6 chars)</Label>
                  <Input id="su-pw" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="su-heard">How did you hear about us?</Label>
                  <Select value={heardFrom} onValueChange={setHeardFrom}>
                    <SelectTrigger id="su-heard">
                      <SelectValue placeholder="Choose one" />
                    </SelectTrigger>
                    <SelectContent>
                      {HEARD_FROM_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-full h-11">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* Skip */}
          <Link
            to="/"
            className="mt-5 block text-center text-sm text-muted-foreground hover:text-foreground transition"
          >
            Skip for now
          </Link>
        </div>

        {/* Footer note */}
        <p className="mt-5 text-center text-xs text-muted-foreground">
          By continuing, you agree to our{" "}
          <Link to="/terms" className="underline hover:text-foreground">Terms</Link>{" "}
          and{" "}
          <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
        </p>
      </div>
    </section>
  );
}
